"use client";

import logger from "@/utils/logger";
// hooks/useGlobalLogout.ts

import { useEffect, useCallback } from "react";
import { useAuth } from "@/context/auth-context";
import { clearLocalStoragePreservingDeviceId } from "@/services/deviceId";
import { ACCOUNT_INACTIVE_REASON } from "@/services/accountAccessState";

// Constants for localStorage keys
export const LOGOUT_EVENT_KEY = "app_logout_event";
export const AUTH_STATE_KEY = "app_auth_state";
const DISPLAY_LOCK_KEY = "timecard_display_lock";
const DISPLAY_LOCK_HEARTBEAT_KEY = "timecard_display_lock_heartbeat";

export const useGlobalLogout = () => {
    const { isAuthenticated, logout } = useAuth();

    // Function to trigger logout across all tabs
    const triggerGlobalLogout = useCallback(async () => {
        logger.info("🚀 Triggering global logout across all tabs...");

        try {
            // Set logout event in localStorage to notify other tabs
            const logoutEvent = {
                timestamp: Date.now(),
                reason: "user_logout",
                tabId: Math.random().toString(36).substr(2, 9),
            };

            localStorage.setItem(LOGOUT_EVENT_KEY, JSON.stringify(logoutEvent));
            localStorage.removeItem(AUTH_STATE_KEY);
            localStorage.removeItem(DISPLAY_LOCK_KEY);
            localStorage.removeItem(DISPLAY_LOCK_HEARTBEAT_KEY);

            // Call the existing logout function
            await logout({ redirectTo: null });

            // Use a hard navigation so the next document loads from a fully
            // cleared client state after logout.
            window.location.replace("/");
        } catch (error) {
            console.error("❌ Error during global logout:", error);
            // Force cleanup even if logout fails
            clearLocalStoragePreservingDeviceId();
            sessionStorage.clear();
            window.location.replace("/");
        }
    }, [logout]);

    // Function to handle logout from another tab
    const handleLogoutFromOtherTab = useCallback(
        async (reason?: string, redirectPath?: string) => {
            logger.info("🔄 Logout detected from another tab, logging out...");

            const nextPath =
                redirectPath ||
                (reason === ACCOUNT_INACTIVE_REASON ? "/403" : "/");

            try {
                // Call logout function (this should handle cleanup)
                localStorage.removeItem(DISPLAY_LOCK_KEY);
                localStorage.removeItem(DISPLAY_LOCK_HEARTBEAT_KEY);
                await logout({ redirectTo: null });

                // Force redirect
                window.location.replace(nextPath);
            } catch (error) {
                console.error(
                    "❌ Error handling logout from other tab:",
                    error
                );
                // Force cleanup
                clearLocalStoragePreservingDeviceId();
                sessionStorage.clear();
                window.location.replace(nextPath);
            }
        },
        [logout]
    );

    // Listen for storage changes (logout events from other tabs)
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        const handleStorageChange = (event: StorageEvent) => {
            // Only handle logout events
            if (event.key === LOGOUT_EVENT_KEY && event.newValue) {
                try {
                    const logoutEvent = JSON.parse(event.newValue) as {
                        reason?: string;
                        redirectPath?: string;
                    };
                    logger.info(
                        "🔔 Logout event detected from another tab:",
                        logoutEvent
                    );

                    // Trigger logout in this tab
                    handleLogoutFromOtherTab(
                        logoutEvent.reason,
                        logoutEvent.redirectPath
                    );
                } catch (error) {
                    console.error("❌ Error parsing logout event:", error);
                }
            }
        };

        // Add event listener
        window.addEventListener("storage", handleStorageChange);

        // Set auth state to indicate this tab is authenticated
        localStorage.setItem(
            AUTH_STATE_KEY,
            JSON.stringify({
                timestamp: Date.now(),
                authenticated: true,
            })
        );

        // Cleanup on unmount
        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, [isAuthenticated, handleLogoutFromOtherTab]);

    // Keep auth marker healthy on focus/visibility changes.
    // Missing marker should not force-logout authenticated users.
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                // Repair marker if it was transiently removed.
                const authState = localStorage.getItem(AUTH_STATE_KEY);
                if (!authState) {
                    logger.info(
                        "🔔 Auth marker missing on tab visibility; restoring marker."
                    );
                    localStorage.setItem(
                        AUTH_STATE_KEY,
                        JSON.stringify({
                            timestamp: Date.now(),
                            authenticated: true,
                            source: "visibility-repair",
                        })
                    );
                }
            }
        };

        const handleFocus = () => {
            // Repair marker if it was transiently removed.
            const authState = localStorage.getItem(AUTH_STATE_KEY);
            if (!authState) {
                logger.info(
                    "🔔 Auth marker missing on focus; restoring marker."
                );
                localStorage.setItem(
                    AUTH_STATE_KEY,
                    JSON.stringify({
                        timestamp: Date.now(),
                        authenticated: true,
                        source: "focus-repair",
                    })
                );
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleFocus);

        return () => {
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
            window.removeEventListener("focus", handleFocus);
        };
    }, [isAuthenticated]);

    return {
        triggerGlobalLogout,
    };
};
